import { useMemo } from "react";
import dayjs from "dayjs";
import { useGetMissionControlTenantTotalInventoryQuery } from "src/store/api/generatedApi";

export interface InventoryDataPoint {
  value: number;
  label: string;
}

export const formatInventoryValue = (value: number) =>
  Math.round(value).toLocaleString();

interface InventoryPoint {
  inventory_date: string;
  total_inventory_mass: number;
}

const aggregateInventory = (
  allSeries: InventoryPoint[][]
): InventoryPoint[] => {
  const byDate = new Map<string, number>();

  allSeries.flat().forEach((point) => {
    const date = point.inventory_date;
    byDate.set(date, (byDate.get(date) ?? 0) + point.total_inventory_mass);
  });

  return [...byDate.entries()]
    .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
    .map(([inventory_date, total_inventory_mass]) => ({
      inventory_date,
      total_inventory_mass,
    }));
};

export const useTenantInventory = (tenantNames: string[]) => {
  const primaryTenant = tenantNames[0] ?? "";
  const secondaryTenant = tenantNames[1] ?? "";

  const { data: primaryInventoryData, isLoading: primaryLoading } =
    useGetMissionControlTenantTotalInventoryQuery(
      { tenantName: primaryTenant },
      { skip: primaryTenant.length === 0 }
    );
  const { data: secondaryInventoryData, isLoading: secondaryLoading } =
    useGetMissionControlTenantTotalInventoryQuery(
      { tenantName: secondaryTenant },
      { skip: secondaryTenant.length === 0 }
    );

  const isLoading = primaryLoading || secondaryLoading;

  const series = useMemo(() => {
    const allSeries = [
      primaryInventoryData?.series ?? [],
      secondaryInventoryData?.series ?? [],
    ].filter((points) => points.length > 0) as InventoryPoint[][];
    return aggregateInventory(allSeries);
  }, [primaryInventoryData, secondaryInventoryData]);

  const hasInventoryData = series.length > 0;

  const latestInventory = useMemo(() => {
    if (!series.length) return null;
    return series[series.length - 1];
  }, [series]);

  const inventoryDataPoints = useMemo((): InventoryDataPoint[] => {
    if (!series.length) return [];
    return series.map((s) => ({
      value: s.total_inventory_mass,
      label: dayjs(s.inventory_date).format("D MMM"),
    }));
  }, [series]);

  const formattedDate = useMemo(() => {
    if (!latestInventory) return "";
    return dayjs(latestInventory.inventory_date).format("MMM YYYY");
  }, [latestInventory]);

  return {
    isLoading,
    hasInventoryData,
    latestInventory,
    inventoryDataPoints,
    formattedDate,
  };
};
